export interface PreviousImportColumn {
    columnKey?: string | null;
    displayName?: string | null;
}

export interface PreselectedMapping {
    /** Required field name → worksheet header. */
    mappings: Record<string, string>;
    /** Worksheet headers that were additional columns last time. */
    additionalHeaders: string[];
}

const normalise = (value: string): string =>
    value.replace(/\s+/g, '').toLowerCase();

/**
 * Matches the columns saved by the previous import against the headers of the newly uploaded worksheet,
 * as FASORT reused the stored mapping on a later import. Only headers that exist in the new file are
 * selected, so anything renamed or missing is left for the user to map.
 */
export const preselectFromPreviousImport = (
    previousColumns: PreviousImportColumn[],
    headers: string[],
    requiredFieldNames: string[],
    maxAdditionalColumns?: number
): PreselectedMapping => {
    const headerByName = new Map<string, string>();
    headers.forEach((header) => {
        const key = normalise(header);
        if (key.length > 0 && !headerByName.has(key)) {
            headerByName.set(key, header);
        }
    });

    const required = new Set(requiredFieldNames.map(normalise));
    const used = new Set<string>();
    const mappings: Record<string, string> = {};

    requiredFieldNames.forEach((fieldName) => {
        const previous = previousColumns.find(
            (column) =>
                normalise(column.columnKey ?? '') === normalise(fieldName)
        );
        const header = headerByName.get(normalise(previous?.displayName ?? ''));

        if (header && !used.has(header)) {
            mappings[fieldName] = header;
            used.add(header);
        }
    });

    const additionalHeaders: string[] = [];
    previousColumns
        .filter((column) => !required.has(normalise(column.columnKey ?? '')))
        .forEach((column) => {
            const header = headerByName.get(
                normalise(column.displayName ?? '')
            );

            if (
                header &&
                !used.has(header) &&
                (maxAdditionalColumns === undefined ||
                    additionalHeaders.length < maxAdditionalColumns)
            ) {
                additionalHeaders.push(header);
                used.add(header);
            }
        });

    return { mappings, additionalHeaders };
};
